import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CanDisable, mixinDisabled } from '@angular/material/core';

import { SelectionChange } from '@angular/cdk/collections';
import { AsyncPipe, NgIf } from '@angular/common';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { FlexModule } from '@angular/flex-layout/flex';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import {
  Countertop,
  CountertopComponentAvailability,
  CountertopEdgeBanding,
  CountertopSlot,
} from '../../models/cutting/public-api';
import { ClampJoint, MatBaseGroupPostforming } from '../../models/user/cutting-settings.model';
import { CountertopSettingsStateService } from './countertop-settings-state.service';
import { CountertopTypeSelectComponent } from './countertop-type-select/countertop-type-select.component';
import { CutoutsComponent } from './cutouts/cutouts.component';
import { EdgeBandingComponent } from './edge-banding/edge-banding.component';
import { FaceBandingComponent } from './face-banding/face-banding.component';
import { FastenersComponent } from './fasteners/fasteners.component';
import { MaterialsComponent } from './materials/materials.component';
import { PanelsJointComponent } from './panels-joint/panels-joint.component';
import { SizesComponent } from './sizes/sizes.component';
import { SlotsComponent } from './slots/slots.component';

export class CountertopSettingsBase {}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const _CountertopSettingsMixinBase = mixinDisabled(CountertopSettingsBase);

@Component({
  selector: 'csl-countertop-settings',
  templateUrl: './countertop-settings.component.html',
  viewProviders: [CountertopSettingsStateService],
  // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
  inputs: ['disabled'],
  standalone: true,
  imports: [
    FlexModule,
    MaterialsComponent,
    SizesComponent,
    MatExpansionModule,
    ExtendedModule,
    NgIf,
    AsyncPipe,
    MatDividerModule,
    FastenersComponent,
    SlotsComponent,
    CountertopTypeSelectComponent,
    EdgeBandingComponent,
    CutoutsComponent,
    FaceBandingComponent,
    PanelsJointComponent,
  ],
})
export class CountertopSettingsComponent extends _CountertopSettingsMixinBase implements OnChanges, CanDisable {
  @Input() countertop: Countertop;
  @Input() userId: number;
  @Input() showMaterialsPrice: boolean;
  @Input() currencySymbol = '';
  @Input() showQuantityInStock: boolean;
  @Input() postformingGroups?: MatBaseGroupPostforming[];

  @Input() componentsAvailability: CountertopComponentAvailability;

  @Input() panelConnectors: ClampJoint[] = [];

  @Output() countertopChange = this.countertopSettingsService.countertop$;

  @Output() valid: BehaviorSubject<boolean> = new BehaviorSubject(false);

  @Output() edgeBandingSelectionChange: EventEmitter<{ sideNumber: number; edgeBanding: CountertopEdgeBanding }[]> =
    new EventEmitter();

  @Output() slotSelectionChange: EventEmitter<SelectionChange<CountertopSlot>> = new EventEmitter();

  _countertop: Countertop;
  countertop$: Observable<Countertop>;

  get isMaterialSelected(): boolean {
    return !!this._countertop?.lines?.[0]?.material;
  }

  get showFasteners(): boolean {
    return this.componentsAvailability?.fasteners;
  }

  get showFaceBanding(): boolean {
    return this.componentsAvailability?.faceBanding;
  }

  get showEdgeBanding(): boolean {
    return this.componentsAvailability?.edgeBanding;
  }

  get showSlots(): boolean {
    return this.componentsAvailability?.slots;
  }

  get showCutouts(): boolean {
    return this.componentsAvailability?.cutouts;
  }

  constructor(private countertopSettingsService: CountertopSettingsStateService) {
    super();

    this.countertop$ = this.countertopSettingsService.countertop$.pipe(tap(c => (this._countertop = c)));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.userId?.currentValue) {
      this.countertopSettingsService.userId = +changes.userId.currentValue;
    }

    if (changes?.countertop?.currentValue) {
      this.countertopSettingsService.setMaterial((changes.countertop.currentValue as Countertop).material);
      this.countertopSettingsService.updateCountertop(changes.countertop.currentValue);
    }
  }
}
